import React, { useState, useEffect } from 'react';
import { Search, Download, Eye, Calendar, Mail, Phone, Briefcase } from 'lucide-react';
import { toast } from 'sonner';
import api from '../services/api';
import './AdminDashboardPage.css';

const AdminDashboardPage = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [viewModalOpen, setViewModalOpen] = useState(false);

  useEffect(() => {
    fetchRequests();
  }, []);

  const fetchRequests = async (search = '') => {
    try {
      setLoading(true);
      const response = await api.getBackgroundCheckRequests(search);
      setRequests(response.data.requests);
    } catch (error) {
      console.error('Error fetching requests:', error);
      toast.error('Failed to load requests');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchRequests(searchQuery);
  };

  const handleViewRequest = async (request) => {
    try {
      const response = await api.getBackgroundCheckRequest(request._id);
      setSelectedRequest(response.data.request);
      setViewModalOpen(true);
    } catch (error) {
      console.error('Error fetching request details:', error);
      toast.error('Failed to load request details');
    }
  };

  const handleDownloadResume = async (requestId, fileName) => {
    try {
      const response = await api.downloadResume(requestId);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Resume downloaded successfully');
    } catch (error) {
      console.error('Error downloading resume:', error);
      toast.error('Failed to download resume');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div className="admin-dashboard-page" data-testid="admin-dashboard-page">
      <div className="container">
        {/* Header */}
        <div className="dashboard-header">
          <div>
            <h1 data-testid="dashboard-title">Background Check Requests</h1>
            <p className="dashboard-subtitle" data-testid="dashboard-subtitle">
              Manage and review all background check submissions
            </p>
          </div>
          <div className="dashboard-stats">
            <div className="stat-badge">
              <span className="stat-number">{requests.length}</span>
              <span className="stat-label">Total Requests</span>
            </div>
          </div>
        </div>

        {/* Search Bar */}
        <div className="dashboard-search">
          <form onSubmit={handleSearch} className="search-form" data-testid="search-form">
            <div className="search-input-wrapper">
              <Search size={20} className="search-icon" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search by name, email, or position..."
                className="search-input"
                data-testid="search-input"
              />
            </div>
            <button type="submit" className="btn btn-primary" data-testid="search-btn">
              Search
            </button>
            {searchQuery && (
              <button
                type="button"
                onClick={() => {
                  setSearchQuery('');
                  fetchRequests('');
                }}
                className="btn btn-secondary"
                data-testid="clear-search-btn"
              >
                Clear
              </button>
            )}
          </form>
        </div>

        {/* Requests Table */}
        <div className="dashboard-content">
          {loading ? (
            <div className="loading-container" data-testid="loading">
              <div className="spinner"></div>
              <p>Loading requests...</p>
            </div>
          ) : requests.length === 0 ? (
            <div className="empty-state" data-testid="empty-state">
              <div className="empty-icon">
                <Briefcase size={48} />
              </div>
              <h3>No Requests Found</h3>
              <p>
                {searchQuery
                  ? 'No requests match your search criteria. Try a different search term.'
                  : 'There are no background check requests yet.'}
              </p>
            </div>
          ) : (
            <div className="requests-table-container">
              <table className="requests-table" data-testid="requests-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Position</th>
                    <th>Submitted</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {requests.map((request, index) => (
                    <tr key={request._id} data-testid={`request-row-${index}`}>
                      <td className="name-cell">{request.full_name}</td>
                      <td className="email-cell">{request.email}</td>
                      <td className="position-cell">{request.current_position}</td>
                      <td className="date-cell">{formatDate(request.created_at)}</td>
                      <td>
                        <span className="status-badge status-pending">
                          {request.status || 'pending'}
                        </span>
                      </td>
                      <td className="actions-cell">
                        <button
                          onClick={() => handleViewRequest(request)}
                          className="action-btn view-btn"
                          title="View Details"
                          data-testid={`view-btn-${index}`}
                        >
                          <Eye size={18} />
                        </button>
                        <button
                          onClick={() => handleDownloadResume(request._id, request.resume_filename)}
                          className="action-btn download-btn"
                          title="Download Resume"
                          data-testid={`download-btn-${index}`}
                        >
                          <Download size={18} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* View Modal */}
      {viewModalOpen && selectedRequest && (
        <div className="modal-overlay" onClick={() => setViewModalOpen(false)} data-testid="view-modal">
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Request Details</h2>
              <button
                onClick={() => setViewModalOpen(false)}
                className="modal-close"
                data-testid="close-modal-btn"
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              <div className="detail-grid">
                <div className="detail-item">
                  <div className="detail-label">
                    <Calendar size={18} />
                    Submitted
                  </div>
                  <div className="detail-value">{formatDate(selectedRequest.created_at)}</div>
                </div>
                <div className="detail-item">
                  <div className="detail-label">
                    <Briefcase size={18} />
                    Status
                  </div>
                  <div className="detail-value">
                    <span className="status-badge status-pending">
                      {selectedRequest.status || 'pending'}
                    </span>
                  </div>
                </div>
              </div>

              <div className="detail-section">
                <h3>Personal Information</h3>
                <div className="detail-grid">
                  <div className="detail-item">
                    <div className="detail-label">Full Name</div>
                    <div className="detail-value">{selectedRequest.full_name}</div>
                  </div>
                  <div className="detail-item">
                    <div className="detail-label">
                      <Mail size={18} />
                      Email
                    </div>
                    <div className="detail-value">
                      <a href={`mailto:${selectedRequest.email}`}>{selectedRequest.email}</a>
                    </div>
                  </div>
                  <div className="detail-item">
                    <div className="detail-label">
                      <Phone size={18} />
                      Phone
                    </div>
                    <div className="detail-value">
                      <a href={`tel:${selectedRequest.phone}`}>{selectedRequest.phone}</a>
                    </div>
                  </div>
                </div>
              </div>

              <div className="detail-section">
                <h3>Professional Information</h3>
                <div className="detail-grid">
                  <div className="detail-item">
                    <div className="detail-label">Current Position</div>
                    <div className="detail-value">{selectedRequest.current_position}</div>
                  </div>
                  {selectedRequest.linkedin_profile && (
                    <div className="detail-item">
                      <div className="detail-label">LinkedIn Profile</div>
                      <div className="detail-value">
                        <a href={selectedRequest.linkedin_profile} target="_blank" rel="noopener noreferrer">
                          View Profile
                        </a>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="detail-section">
                <h3>Resume</h3>
                <div className="resume-info">
                  <div className="resume-name">{selectedRequest.resume_filename}</div>
                  <button
                    onClick={() => handleDownloadResume(selectedRequest._id, selectedRequest.resume_filename)}
                    className="btn btn-primary"
                    data-testid="modal-download-btn"
                  >
                    <Download size={18} />
                    Download Resume
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboardPage;
